export type Rol = 'propietario' | 'cajero' | 'contador';

export type MetodoPago =
  | 'efectivo'
  | 'tarjeta'
  | 'nequi'
  | 'daviplata'
  | 'breb'
  | 'transferencia'
  | 'otro';

export type FuenteGasto = 'qr' | 'foto' | 'manual';
export type CategoriaGasto = 'insumos' | 'fijos' | 'servicios' | 'nomina' | 'otros';
export type UnidadInsumo = 'unidad' | 'g' | 'ml';

/**
 * Los tributos de una compra. `otro` existe porque el proveedor cobra lo que su documento declare y el
 * catálogo de tributos de la DIAN no está en la base: sin `otro`, todo lo que no es IVA ni INC habría
 * que llamarlo IVA.
 */
export type TipoImpuestoGasto = 'iva' | 'inc' | 'otro';

export interface ImpuestoDeGasto {
  tipo: TipoImpuestoGasto;
  /** Obligatorio cuando el tipo es `otro`: es lo único que dice de qué tributo se trata. */
  nombre: string | null;
  /** El código del catálogo de la DIAN, tal como lo declara el documento del proveedor. */
  codigoDian: string | null;
  /** `null` cuando el documento da el monto y no la tarifa. */
  tarifa: number | null;
  monto: number;
}

/** Un impuesto tal como se cobra: en Colombia el IVA y el consumo pueden convivir. */
interface ImpuestoAplicado {
  nombre: string;
  tipo: 'iva' | 'inc';
  tarifa: number;
  /** Si el impuesto se cobra sobre el precio con descuento; lo decide el negocio al crearlo. */
  reduceBase: boolean;
}

/** Rebaja a un producto con un descuento del dueño: porcentaje, monto fijo por unidad, precio especial o lleva y
 *  paga. `tipo` y `valor` son los del descuento, o los que se escribieron si es el libre. */
export interface DescuentoDeLinea {
  descuentoId: string;
  nombre: string;
  libre: boolean;
  tipo: 'pct' | 'monto_unidad' | 'precio_unidad' | 'lleva_paga';
  valor: number;
  /** Una promoción automática: la pone el mostrador y el cajero solo la quita. */
  automatico?: boolean;
  lleva?: number | null;
  paga?: number | null;
  /** Veces que el lleva y paga se repite en una venta. */
  maxVeces?: number | null;
  /** Unidades de la venta que reciben el porcentaje o el monto fijo. */
  maxUnidades?: number | null;
}

/** Rebaja a la venta completa, sobre lo que queda tras las rebajas de producto. Uno de monto fijo se acota a ese neto. */
export interface DescuentoDeVenta {
  descuentoId: string;
  nombre: string;
  libre: boolean;
  tipo: 'pct' | 'monto';
  valor: number;
}

/** El del catálogo del negocio: se puede marcar y desmarcar, así que lleva id. */
export interface ImpuestoDeProducto extends ImpuestoAplicado {
  id: string;
}

export interface LineaMostrador {
  sku: string;
  nombre: string;
  productoId: string;
  cantidad: number;
  precioUnit: number;
  impuestos: ImpuestoAplicado[];
  descuento?: DescuentoDeLinea | null;
  /** La categoría del producto, que decide qué promociones lo cubren. */
  categoria?: string;
  /** Las promociones que el cajero quitó en esta línea: no se vuelven a poner solas. */
  promocionQuitada?: string[];
}

export interface TotalesVenta {
  /** Antes de cualquier descuento. */
  bruto: number;
  descuentoLineas: number;
  descuentoVenta: number;
  /** Neto de los dos descuentos: la base de la venta. */
  subtotal: number;
  impuesto: number;
  total: number;
}

export interface Producto {
  id: string;
  sku: string;
  ean: string | null;
  nombre: string;
  categoria: string;
  precio: number;
  impuestos: ImpuestoDeProducto[];
  activo: boolean;
}

export const DENOMINACIONES_COP = [
  100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50,
] as const;

/**
 * El arqueo se cuenta en dos montones, que es como está la plata en el cajón. El corte va en 2.000: el
 * billete de 1.000 salió de circulación y hoy esa denominación es moneda.
 */
export const BILLETES_COP = DENOMINACIONES_COP.filter((d) => d >= 2000);
export const MONEDAS_COP = DENOMINACIONES_COP.filter((d) => d < 2000);

/** Las formas de pago de una venta: el mismo tope que `registrar_venta` (`TN_PAGOS_FUERA_DE_RANGO`). */
export const PAGOS_POR_VENTA_MAXIMO = 5;
